import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Select,
  DataTable,
  EmptyState,
  Spinner,
  Box,
} from '@shopify/polaris';

import { useStore } from '../providers/StoreProvider';
import { analyticsApi } from '../utils/api';
import AppLayout from '../components/AppLayout';
import PremiumFeatureLock from '../components/PremiumFeatureLock';
import ExperimentResultsCard from '../components/ExperimentResultsCard';

function Analytics() {
  const navigate = useNavigate();
  const { bars, isPremium, experiments } = useStore();

  const [selectedBar, setSelectedBar] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    if (!isPremium) return;

    setIsLoading(true);
    
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];
      
      if (selectedBar === 'all') {
        const data = await analyticsApi.getSummary(startDate, endDate);
        setAnalytics(data);
      } else {
        const data = await analyticsApi.getBarAnalytics(selectedBar, startDate, endDate);
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
    
    setIsLoading(false);
  }, [isPremium, selectedBar, dateRange]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  const barOptions = [
    { label: 'All Bars', value: 'all' },
    ...bars.map(bar => ({ label: bar.name, value: String(bar.id) })),
  ];
  
  const dateRangeOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
  ];
  
  if (!isPremium) {
    return (
      <AppLayout>
        <Page title="Analytics">
          <PremiumFeatureLock isPremium={false} feature="Performance Tracking" fullPage>
            <EmptyState
              heading="Unlock Performance Analytics"
              action={{
                content: 'Upgrade to Premium',
                onAction: () => navigate('/app/billing'),
              }}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                Track impressions, clicks, conversions, and more with Premium.
                See which bars perform best and optimize your strategy.
              </p>
            </EmptyState>
          </PremiumFeatureLock>
        </Page>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <Page title="Analytics">
        <Layout>
          {/* Filters */}
          <Layout.Section>
            <Card>
              <InlineStack gap="400">
                <Box minWidth="200px">
                  <Select
                    label="Bar"
                    options={barOptions}
                    value={selectedBar}
                    onChange={setSelectedBar}
                  />
                </Box>
                <Box minWidth="200px">
                  <Select
                    label="Date Range"
                    options={dateRangeOptions}
                    value={dateRange}
                    onChange={setDateRange}
                  />
                </Box>
              </InlineStack>
            </Card>
          </Layout.Section>
          
          {/* Stats Overview */}
          {isLoading ? (
            <Layout.Section>
              <Card>
                <Box padding="800">
                  <InlineStack align="center">
                    <Spinner size="large" />
                  </InlineStack>
                </Box>
              </Card>
            </Layout.Section>
          ) : analytics?.totals ? (
            <>
              <Layout.Section>
                <InlineStack gap="400">
                  <Card>
                    <BlockStack gap="200">
                      <Text tone="subdued">Impressions</Text>
                      <Text variant="headingLg">
                        {analytics.totals.impressions?.toLocaleString() || 0}
                      </Text>
                    </BlockStack>
                  </Card>
                  
                  <Card>
                    <BlockStack gap="200">
                      <Text tone="subdued">Clicks</Text>
                      <Text variant="headingLg">
                        {analytics.totals.clicks?.toLocaleString() || 0}
                      </Text>
                    </BlockStack>
                  </Card>
                  
                  <Card>
                    <BlockStack gap="200">
                      <Text tone="subdued">Click Rate</Text>
                      <Text variant="headingLg">
                        {(analytics.totals.click_rate || 0).toFixed(2)}%
                      </Text>
                    </BlockStack>
                  </Card>
                  
                  <Card>
                    <BlockStack gap="200">
                      <Text tone="subdued">Conversions</Text>
                      <Text variant="headingLg">
                        {analytics.totals.conversions?.toLocaleString() || 0}
                      </Text>
                    </BlockStack>
                  </Card>
                </InlineStack>
              </Layout.Section>
              
              {/* Bar Performance Table */}
              {selectedBar === 'all' && analytics.bars && (
                <Layout.Section>
                  <Card>
                    <BlockStack gap="300">
                      <Text variant="headingMd">Bar Performance</Text>
                      <DataTable
                        columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                        headings={['Bar', 'Impressions', 'Clicks', 'Conversions', 'Revenue']}
                        rows={analytics.bars.map(bar => [
                          bar.name,
                          bar.impressions?.toLocaleString() || '0',
                          bar.clicks?.toLocaleString() || '0',
                          bar.conversions?.toLocaleString() || '0',
                          `$${(bar.revenue || 0).toFixed(2)}`,
                        ])}
                      />
                    </BlockStack>
                  </Card>
                </Layout.Section>
              )}
              
              {/* Daily Breakdown */}
              {analytics.daily && analytics.daily.length > 0 && (
                <Layout.Section>
                  <Card>
                    <BlockStack gap="300">
                      <Text variant="headingMd">Daily Breakdown</Text>
                      <DataTable
                        columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                        headings={['Date', 'Impressions', 'Clicks', 'Conversions']}
                        rows={analytics.daily.slice(-10).map(day => [
                          new Date(day.date).toLocaleDateString(),
                          day.impressions?.toLocaleString() || '0',
                          day.clicks?.toLocaleString() || '0',
                          day.conversions?.toLocaleString() || '0',
                        ])}
                      />
                    </BlockStack>
                  </Card>
                </Layout.Section>
              )}

              {/* A/B Test Results */}
              {experiments.filter(e => e.status === 'running' || e.status === 'stopped').length > 0 && (
                <Layout.Section>
                  <BlockStack gap="400">
                    <Text variant="headingMd">A/B Test Results</Text>
                    {experiments
                      .filter(e => e.status === 'running' || e.status === 'stopped')
                      .map(exp => {
                        const endDate = new Date().toISOString().split('T')[0];
                        const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000)
                          .toISOString().split('T')[0];
                        return (
                          <ExperimentResultsCard
                            key={exp.id}
                            experiment={exp}
                            startDate={startDate}
                            endDate={endDate}
                          />
                        );
                      })
                    }
                  </BlockStack>
                </Layout.Section>
              )}
            </>
          ) : (
            <Layout.Section>
              <Card>
                <EmptyState
                  heading="No analytics data yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Analytics data will appear here once visitors start 
                    interacting with your shipping bars.
                  </p>
                </EmptyState>
              </Card>
            </Layout.Section>
          )}
        </Layout>
      </Page>
    </AppLayout>
  );
}

export default Analytics;
